// Modules
import { useApolloClient } from '@apollo/client'
import { useLogInfoStore } from 'redesign/store'

// Application
import {
    MessageChangePassword,
    ChangePassword,
    CheckToken,
    SendResetToken,
    SendOTPToken,
    CheckOTPToken,
} from 'services/gql/recoverpassword/mutation'
import { ViewerSearchLoginByCPF } from 'services/gql/recoverpassword/query'
import { ContactType } from 'typings'
import {
    ViewerLogin,
    MessageChangePasswordData,
    ChangePasswordInfoInput,
    ChangePasswordData,
    PatientToken,
    PatienTokenResponse,
    SendResetTokenData,
    OTPTokenData,
    AuthenticateOtp,
} from 'typings/recoverpassword'
import { convertToNumber } from 'utils'

export const useRecoverService = () => {
    const client = useApolloClient()
    const { logInfo } = useLogInfoStore()

    const getContactList = async (username: string): Promise<{ email: string; mobile: string }> => {
        const { data } = await client.query<ViewerLogin>({
            query: ViewerSearchLoginByCPF,
            variables: {
                username,
            },
        })

        return {
            email: data.viewerSearchLoginByCPF.email,
            mobile: data.viewerSearchLoginByCPF.mobile,
        }
    }

    const messageChangePassword = async (
        username: string,
        type: ContactType,
    ): Promise<
        | {
              phone: string
              email: string
              message: string
          }
        | undefined
    > => {
        const { data } = await client.mutate<MessageChangePasswordData>({
            mutation: MessageChangePassword,
            variables: {
                username: username,
                contact: type,
                logInfo,
            },
        })

        return data?.messageChangePassword
    }

    const sendResetToken = async (username: string, dispatchType: ContactType, contactId: number): Promise<string | undefined> => {
        const { data } = await client.mutate<SendResetTokenData>({
            mutation: SendResetToken,
            variables: {
                resetInput: {
                    userName: username,
                    dispatchType,
                    contactId,
                },
                logInfo,
            },
        })

        return data?.sendResetToken.status
    }

    const sendOTPToken = async (username: string, dispatchType: ContactType, contactId: number): Promise<OTPTokenData> => {
        const { data } = await client.mutate<OTPTokenData>({
            mutation: SendOTPToken,
            variables: {
                generateOtpInput: {
                    cpf: username,
                    dispatchType,
                    contactId,
                },
                logInfo,
            },
        })

        return data!
    }

    const changePassword = async ({ username, token, newPassword }: ChangePasswordInfoInput): Promise<ChangePasswordData> => {
        const { data } = await client.mutate<ChangePasswordData>({
            mutation: ChangePassword,
            variables: {
                username: convertToNumber(username),
                token,
                newPassword,
                logInfo,
            },
        })

        return data!
    }

    const checkToken = async (patientToken: PatientToken): Promise<PatienTokenResponse> => {
        const { data } = await client.mutate<PatienTokenResponse>({
            mutation: CheckToken,
            variables: {
                patientToken,
            },
        })

        return data!
    }

    const checkOTPToken = async (key: string, password: string, cpf: string): Promise<AuthenticateOtp> => {
        const { data } = await client.mutate({
            mutation: CheckOTPToken,
            variables: {
                key,
                password,
                cpf,
                logInfo,
            },
        })

        return data!
    }

    return {
        sendResetToken,
        sendOTPToken,
        getContactList,
        messageChangePassword,
        changePassword,
        checkToken,
        checkOTPToken,
    }
}
